import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  Edit3,
  Expand,
  Filter,
  LocateFixed,
  Network,
  Search,
  Shrink,
  User,
  Users,
  X,
} from "lucide-react";
import "./PersonTree.css";

const normalize = (value) => String(value || "").trim().toLowerCase();
const getPersonKey = (person) =>
  String(person.documentId || person.id || person.personId || person.name);

export const buildForest = (persons) => {
  const nodeMap = new Map();
  const referenceMap = new Map();

  persons.forEach((person) => {
    const key = getPersonKey(person);
    const node = { key, person, children: [] };
    nodeMap.set(key, node);
    [person.name, person.personId, person.id, person.documentId]
      .filter(Boolean)
      .forEach((reference) => referenceMap.set(normalize(reference), node));
  });

  const roots = [];
  nodeMap.forEach((node) => {
    const parentReference = normalize(node.person.assignedTo);
    const parent = parentReference && parentReference !== "none"
      ? referenceMap.get(parentReference)
      : null;

    if (parent && parent.key !== node.key) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  // Corrupted cyclic data can leave the forest without a root. Showing every
  // node independently keeps the editor usable so the cycle can be repaired.
  return {
    roots: roots.length ? roots : Array.from(nodeMap.values()),
    nodeMap,
  };
};

const PersonTree = ({
  persons = [],
  onPersonClick,
  updatePerson,
  deletePerson,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [depthFilter, setDepthFilter] = useState("all");
  const [zoom, setZoom] = useState(1);
  const [compact, setCompact] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [editingPerson, setEditingPerson] = useState(null);
  const [draggedPerson, setDraggedPerson] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const { roots, nodeMap } = useMemo(() => buildForest(persons), [persons]);
  const availableTypes = useMemo(
    () => [...new Set(persons.map((person) => person.type).filter(Boolean))].sort(),
    [persons]
  );

  const visibleRoots = useMemo(() => {
    const query = normalize(searchTerm);
    const maxDepth = depthFilter === "all" ? Infinity : Number(depthFilter);

    const filterNode = (node, depth, lineage = new Set()) => {
      if (lineage.has(node.key) || depth > maxDepth) return null;
      const nextLineage = new Set(lineage);
      nextLineage.add(node.key);

      const children = node.children
        .map((child) => filterNode(child, depth + 1, nextLineage))
        .filter(Boolean);
      const searchMatch =
        !query ||
        normalize(node.person.name).includes(query) ||
        normalize(node.person.personId).includes(query) ||
        normalize(node.person.assignedTo).includes(query);
      const typeMatch = typeFilter === "all" || node.person.type === typeFilter;

      if ((searchMatch && typeMatch) || children.length > 0) {
        return { ...node, children };
      }
      return null;
    };

    return roots.map((root) => filterNode(root, 0)).filter(Boolean);
  }, [roots, searchTerm, typeFilter, depthFilter]);

  const toggleCollapsed = (key) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isDescendant = (ancestorKey, candidateKey) => {
    const start = nodeMap.get(ancestorKey);
    if (!start) return false;
    const pending = [...start.children];
    const visited = new Set();

    while (pending.length) {
      const node = pending.shift();
      if (!node || visited.has(node.key)) continue;
      if (node.key === candidateKey) return true;
      visited.add(node.key);
      pending.push(...node.children);
    }
    return false;
  };

  const reassignPerson = async (newParent) => {
    if (!draggedPerson || !updatePerson) return;
    const draggedKey = getPersonKey(draggedPerson);
    const targetKey = newParent ? getPersonKey(newParent) : null;

    if (
      targetKey === draggedKey ||
      (targetKey && isDescendant(draggedKey, targetKey))
    ) {
      setDraggedPerson(null);
      setDropTarget(null);
      return;
    }

    await updatePerson({
      ...draggedPerson,
      assignedTo: newParent?.name || "None",
    }).catch(() => {});
    setDraggedPerson(null);
    setDropTarget(null);
  };

  const exportTree = () => {
    const file = new Blob([JSON.stringify(persons, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `organizatie-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const openPerson = (person) => {
    setSelectedPerson(person);
    if (onPersonClick) onPersonClick(person);
  };

  const renderNode = (node, lineage = new Set()) => {
    if (lineage.has(node.key)) return null;
    const nextLineage = new Set(lineage);
    nextLineage.add(node.key);
    const isCollapsed = collapsed.has(node.key);
    const hasChildren = node.children.length > 0;
    const isDropTarget = dropTarget === node.key;

    return (
      <li className="organization-tree__branch" key={node.key}>
        <article
          className={`organization-tree__card ${compact ? "organization-tree__card--compact" : ""} ${isDropTarget ? "organization-tree__card--drop" : ""}`}
          draggable={!!updatePerson}
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = "move";
            setDraggedPerson(node.person);
          }}
          onDragEnd={() => {
            setDraggedPerson(null);
            setDropTarget(null);
          }}
          onDragOver={(event) => {
            if (!draggedPerson) return;
            event.preventDefault();
            setDropTarget(node.key);
          }}
          onDragLeave={() => {
            if (dropTarget === node.key) setDropTarget(null);
          }}
          onDrop={(event) => {
            event.preventDefault();
            reassignPerson(node.person);
          }}
          onClick={() => openPerson(node.person)}
        >
          <button
            type="button"
            className="organization-tree__collapse"
            onClick={(event) => {
              event.stopPropagation();
              if (hasChildren) toggleCollapsed(node.key);
            }}
            aria-label={isCollapsed ? "Extinde ramura" : "Restrânge ramura"}
            disabled={!hasChildren}
          >
            {hasChildren
              ? isCollapsed
                ? <ChevronRight size={14} />
                : <ChevronDown size={14} />
              : <span />}
          </button>

          <div className="organization-tree__avatar">
            {normalize(node.person.type).includes("consult")
              ? <Users size={17} />
              : <User size={17} />}
          </div>
          <div className="organization-tree__identity">
            <strong title={node.person.name}>{node.person.name || "Fără nume"}</strong>
          </div>
          {updatePerson && (
            <button
              type="button"
              className="organization-tree__edit"
              onClick={(event) => {
                event.stopPropagation();
                setEditingPerson({ ...node.person });
              }}
              aria-label={`Editează ${node.person.name}`}
            >
              <Edit3 size={14} />
            </button>
          )}
        </article>

        {hasChildren && !isCollapsed && (
          <ul className="organization-tree__children">
            {node.children.map((child) => renderNode(child, nextLineage))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <section className="organization-tree">
      <header className="organization-tree__header">
        <div>
          <span>STRUCTURĂ ORGANIZAȚIONALĂ</span>
          <h3><Network size={22} /> Arborele echipei</h3>
          <p>Trage un card peste altul pentru a schimba coordonatorul.</p>
        </div>
        <div className="organization-tree__stats">
          <span><strong>{persons.length}</strong> persoane</span>
          <span><strong>{roots.length}</strong> rădăcini</span>
        </div>
      </header>

      <div className="organization-tree__controls">
        <label className="organization-tree__search">
          <Search size={16} />
          <input
            type="search"
            placeholder="Caută după nume sau ID"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <button type="button" onClick={() => setSearchTerm("")} aria-label="Șterge căutarea">
              <X size={14} />
            </button>
          )}
        </label>

        <label>
          <Filter size={15} />
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="all">Toate rolurile</option>
            {availableTypes.map((type) => <option value={type} key={type}>{type}</option>)}
          </select>
        </label>

        <label>
          <Network size={15} />
          <select value={depthFilter} onChange={(event) => setDepthFilter(event.target.value)}>
            <option value="all">Toate nivelurile</option>
            <option value="1">Primele 2 niveluri</option>
            <option value="2">Primele 3 niveluri</option>
            <option value="3">Primele 4 niveluri</option>
          </select>
        </label>

        <div className="organization-tree__icon-actions">
          <button
            type="button"
            onClick={() => setZoom((value) => Math.max(0.7, value - 0.1))}
            aria-label="Micșorează arborele"
          >
            <Shrink size={16} />
          </button>
          <span>{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((value) => Math.min(1.4, value + 0.1))}
            aria-label="Mărește arborele"
          >
            <Expand size={16} />
          </button>
          <button
            type="button"
            onClick={() => {
              setZoom(1);
              setCollapsed(new Set());
            }}
            aria-label="Resetează vizualizarea"
          >
            <LocateFixed size={16} />
          </button>
          <button
            type="button"
            className={compact ? "active" : ""}
            onClick={() => setCompact((value) => !value)}
            aria-label="Comută vizualizarea compactă"
          >
            <Users size={16} />
          </button>
          <button type="button" onClick={exportTree} aria-label="Exportă organizația">
            <Download size={16} />
          </button>
        </div>
      </div>

      <div
        className={`organization-tree__root-drop ${draggedPerson ? "visible" : ""}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          reassignPerson(null);
        }}
      >
        Mută la nivelul principal
      </div>

      <div className="organization-tree__viewport">
        {visibleRoots.length > 0 ? (
          <div
            className="organization-tree__canvas"
            style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
          >
            <ul className="organization-tree__roots">
              {visibleRoots.map((root) => renderNode(root))}
            </ul>
          </div>
        ) : (
          <div className="organization-tree__empty">
            <Network size={44} />
            <h4>Nu există rezultate</h4>
            <p>Schimbă filtrele sau termenul de căutare.</p>
          </div>
        )}
      </div>

      {selectedPerson && (
        <div className="organization-tree__modal" role="dialog" aria-modal="true">
          <button className="organization-tree__backdrop" onClick={() => setSelectedPerson(null)} aria-label="Închide" />
          <div className="organization-tree__modal-card">
            <header>
              <div className="organization-tree__avatar"><User size={20} /></div>
              <div>
                <span>DETALII MEMBRU</span>
                <h4>{selectedPerson.name}</h4>
              </div>
              <button type="button" onClick={() => setSelectedPerson(null)} aria-label="Închide"><X size={18} /></button>
            </header>
            <dl>
              <div><dt>ID</dt><dd>{selectedPerson.personId || selectedPerson.id || "—"}</dd></div>
              <div><dt>Rol</dt><dd>{selectedPerson.type || "Membru"}</dd></div>
              <div><dt>Coordonator</dt><dd>{selectedPerson.assignedTo || "Nivel principal"}</dd></div>
              <div><dt>Adăugat</dt><dd>{selectedPerson.createdAt ? new Date(selectedPerson.createdAt).toLocaleDateString("ro-RO") : "—"}</dd></div>
            </dl>
            <footer>
              {updatePerson && (
                <button type="button" onClick={() => {
                  setEditingPerson({ ...selectedPerson });
                  setSelectedPerson(null);
                }}>
                  <Edit3 size={15} /> Editează
                </button>
              )}
              {deletePerson && (
                <button type="button" className="danger" onClick={() => deletePerson(selectedPerson.id)}>
                  Șterge
                </button>
              )}
            </footer>
          </div>
        </div>
      )}

      {editingPerson && (
        <div className="organization-tree__modal" role="dialog" aria-modal="true">
          <button className="organization-tree__backdrop" onClick={() => setEditingPerson(null)} aria-label="Închide" />
          <form
            className="organization-tree__modal-card"
            onSubmit={async (event) => {
              event.preventDefault();
              await updatePerson(editingPerson).catch(() => {});
              setEditingPerson(null);
            }}
          >
            <header>
              <div>
                <span>ORGANIZAȚIE</span>
                <h4>Editează persoana</h4>
              </div>
              <button type="button" onClick={() => setEditingPerson(null)} aria-label="Închide"><X size={18} /></button>
            </header>
            <div className="organization-tree__form">
              <label>Nume<input value={editingPerson.name || ""} onChange={(event) => setEditingPerson((current) => ({ ...current, name: event.target.value }))} required /></label>
              <label>ID persoană<input value={editingPerson.personId || ""} onChange={(event) => setEditingPerson((current) => ({ ...current, personId: event.target.value }))} /></label>
              <label>Rol
                <select value={editingPerson.type || ""} onChange={(event) => setEditingPerson((current) => ({ ...current, type: event.target.value }))}>
                  {[...new Set([...availableTypes, editingPerson.type].filter(Boolean))].map((type) => <option value={type} key={type}>{type}</option>)}
                </select>
              </label>
              <label>Coordonator
                <select value={editingPerson.assignedTo || "None"} onChange={(event) => setEditingPerson((current) => ({ ...current, assignedTo: event.target.value }))}>
                  <option value="None">Nivel principal</option>
                  {persons
                    .filter((person) => getPersonKey(person) !== getPersonKey(editingPerson))
                    .map((person) => <option value={person.name} key={getPersonKey(person)}>{person.name}</option>)}
                </select>
              </label>
            </div>
            <footer>
              <button type="button" className="secondary" onClick={() => setEditingPerson(null)}>Anulează</button>
              <button type="submit">Salvează</button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
};

export default PersonTree;
